import { jsx as _jsx } from "react/jsx-runtime";
import { Navigate } from 'react-router-dom';
import { isNotNil } from '@lucidlink/tscore';
import { wrapper } from '@lucidlink/uicore';
import { APP_PATHS, FILESPACE_PATHS, HEADING_KEYS, LucidLinkConnectFeatureFlagProperties, MigrationFlowFeatureFlagProperties, WORKSPACE_PATHS, WorkspaceOnboardingFlowFeatureFlagProperties, } from '#constants';
import { setHandle } from '#services';
import { FilespaceFooterLayout, FullscreenLayout, HeaderLayout, InviteSupportFooterLayout, LoginSupportFooterLayout, RegisterSupportFooterLayout, WorkspaceFooterLayout, WorkspaceNavigationLayout, WorkspaceSettingsLayout, } from './components';
import { disableForNotSuspendedPredicate, disableForSuspendedPredicate, FeatureFlagGuard, FilespaceAdminGuard, FilespaceCreateRouteGuard, FilespaceLucidLinkConnectRouteGuard, FilespacePermissionRouteGuard, inviteLandingRoutePredicate, inviteRoutePredicate, IsMigratingRouteGuard, loginRoutePredicate, MemberRouteGuard, needsWorkspaceAuthenticationPredicate, needsWorkspaceLockPredicate, NoCodeLoginRoute, privateRoutePredicate, PrivilegeRouteGuard, ProtectedRoute, registerRoutePredicate, RestrictedFilespaceRouteGuard, verifyEmailRoutePredicate, WorkspaceAuthenticationRouteGuard, WorkspaceNativeLayoutRouteGuard, WorkspaceStatusRouteGuard, } from './guards';
import { getRouteObject } from './services';
const childRoutesConfig = [
    {
        lazy: () => import('#features/auth/pages/AwsOffer/AwsOffer.tsx'),
        path: APP_PATHS.awsOffer,
        wrappers: [],
    },
    {
        lazy: () => import('#features/auth/pages/SSOLanding.tsx'),
        path: APP_PATHS.ssoLanding,
        wrappers: [],
    },
    {
        lazy: () => import('#features/filespaces/pages/DirectLink.tsx'),
        path: APP_PATHS.directLink,
        wrappers: [],
    },
    {
        lazy: () => import('#features/auth/pages/InviteLanding.tsx'),
        path: APP_PATHS.inviteLanding,
        wrappers: [
            wrapper(ProtectedRoute, {
                predicateFn: inviteLandingRoutePredicate,
            }),
        ],
    },
    {
        lazy: () => import('#features/auth/pages/ActivateAccount/ActivateAccount.tsx'),
        path: APP_PATHS.activateAccount,
        wrappers: [wrapper(FullscreenLayout)],
    },
    {
        lazy: () => import('#features/auth/pages/Invite/Invite.tsx'),
        path: APP_PATHS.invite,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: inviteRoutePredicate }),
            wrapper(InviteSupportFooterLayout),
        ],
    },
    {
        lazy: () => import('#features/auth/pages/AccountRecovery.tsx'),
        path: APP_PATHS.accountRecovery,
        wrappers: [wrapper(LoginSupportFooterLayout)],
    },
    {
        lazy: () => import('#features/auth/pages/BackupCodeLogin/BackupCodeLogin.tsx'),
        path: APP_PATHS.backupCodeLogin,
        wrappers: [wrapper(LoginSupportFooterLayout)],
    },
    {
        lazy: () => import('#features/auth/pages/PasswordReset/PasswordReset.tsx'),
        path: APP_PATHS.passwordReset,
        wrappers: [wrapper(LoginSupportFooterLayout)],
    },
    {
        lazy: () => import('#features/auth/pages/Login/Login.tsx'),
        path: APP_PATHS.login,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: loginRoutePredicate }),
            wrapper(LoginSupportFooterLayout),
        ],
    },
    {
        lazy: () => import('#features/auth/pages/Registration/Registration.tsx'),
        path: APP_PATHS.register,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: registerRoutePredicate }),
            wrapper(RegisterSupportFooterLayout),
        ],
    },
    {
        lazy: () => import('#features/auth/pages/VerifyEmail.tsx'),
        path: APP_PATHS.verifyEmail,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: verifyEmailRoutePredicate }),
            wrapper(RegisterSupportFooterLayout),
        ],
    },
    {
        lazy: () => import('#features/workspaces/pages/WorkspaceOnboarding/WorkspaceOnboarding.tsx'),
        path: WORKSPACE_PATHS.workspaceOnboarding,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(FeatureFlagGuard, WorkspaceOnboardingFlowFeatureFlagProperties),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.account,
        }),
        lazy: () => import('#features/account/pages/Account/Account.tsx'),
        path: APP_PATHS.account,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(FullscreenLayout),
            wrapper(NoCodeLoginRoute),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceDashboard,
        }),
        lazy: () => import('#features/workspaces/pages/WorkspacesDashboard/WorkspacesDashboard.tsx'),
        path: WORKSPACE_PATHS.workspaces,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceCreate,
        }),
        lazy: () => import('#features/workspaces/pages/WorkspaceCreate/WorkspaceCreate.tsx'),
        path: WORKSPACE_PATHS.workspacesCreate,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceCreate,
        }),
        lazy: () => import('#features/workspaces/pages/NoWorkspaces.tsx'),
        path: WORKSPACE_PATHS.workspacesNoWorkspaces,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceCreate,
        }),
        lazy: () => import('#features/filespaces/pages/FilespaceCreate/FilespaceCreate.tsx'),
        path: WORKSPACE_PATHS.workspaceFilespaceCreate,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'filespaceCrud',
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(FilespaceCreateRouteGuard),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceMigration,
        }),
        lazy: () => import('#features/migration/pages/FilespaceMigration.tsx'),
        path: WORKSPACE_PATHS.filespaceMigrate,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(FeatureFlagGuard, MigrationFlowFeatureFlagProperties),
            wrapper(HeaderLayout),
            wrapper(FullscreenLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceSettings,
        }),
        lazy: Promise.resolve({
            default: () => _jsx(Navigate, { to: "general" }),
        }),
        path: WORKSPACE_PATHS.workspaceSettings,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceSettings,
        }),
        lazy: () => import('#features/workspace-settings/pages/WorkspaceSettingsGeneral/WorkspaceSettingsGeneral.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsGeneral,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.domainManagement,
        }),
        lazy: () => import('#features/workspace-settings/pages/WorkspaceSettingsDomainManagement/WorkspaceSettingsDomainManagement.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsDomainManagement,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'workspaceDomain',
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.members,
        }),
        lazy: () => import('#features/members/pages/WorkspaceSettingsMembers/WorkspaceSettingsMembers.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsMembers,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'membersRead',
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.memberDetails,
        }),
        lazy: () => import('#features/members/pages/WorkspaceSettingsMembersManage/WorkspaceSettingsMembersManage.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsMember,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    OR: ['groupMemberships', 'canAdminFilespaces'],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.groups,
        }),
        lazy: () => import('#features/members/pages/WorkspaceSettingsGroups/WorkspaceSettingsGroups.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsGroups,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'groupsRead',
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.groupDetails,
        }),
        lazy: () => import('#features/members/pages/GroupDetails/GroupDetails.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsGroup,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    OR: ['groupMemberships', 'canAdminFilespaces'],
                },
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({ heading: HEADING_KEYS.developerTools }),
        lazy: () => import('#features/workspace-settings/pages/WorkspaceSettingsDeveloperTools/WorkspaceSettingsDeveloperTools.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsDeveloperTools,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'serviceAccountCrud',
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.serviceAccounts,
        }),
        lazy: () => import('#features/service-accounts/pages/WorkspaceSettingsServiceAccounts/WorkspaceSettingsServiceAccounts.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsServiceAccounts,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'serviceAccountCrud',
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.serviceAccounts,
        }),
        lazy: () => import('#features/service-accounts/pages/ServiceAccountManagement/ServiceAccountManagement.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsServiceAccount,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'serviceAccountCrud',
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspacePermissions,
        }),
        lazy: () => import('#features/permissions/pages/WorkspaceSettingsPermissions/WorkspaceSettingsPermissions.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsPermissions,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        {
                            OR: ['groupsRead', 'membersUpdate'],
                        },
                    ],
                },
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.twoFactorAuthentication,
        }),
        lazy: () => import('#features/workspace-settings/pages/WorkspaceSettingsTwoFactorAuthentication/WorkspaceSettingsTwoFactorAuthentication.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsTwoFactorAuthentication,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'workspaceMfa',
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.ssoSettings,
        }),
        lazy: () => import('#features/workspace-settings/pages/WorkspaceSettingsSso/WorkspaceSettingsSso.tsx'),
        path: WORKSPACE_PATHS.workspaceSettingsSso,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: 'sso',
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceSettingsLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceDashboard,
        }),
        lazy: () => import('#features/workspaces/pages/WorkspaceDetails/WorkspaceDetails.tsx'),
        path: WORKSPACE_PATHS.workspace,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceLock,
        }),
        lazy: () => import('#features/workspaces/pages/WorkspaceLock/WorkspaceLock.tsx'),
        // https://lucidlink.atlassian.net/browse/LUCID-28744
        path: WORKSPACE_PATHS.workspaceLock,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceLockPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.workspaceSuspended,
        }),
        lazy: () => import('#features/workspaces/pages/WorkspaceSuspended.tsx'),
        path: WORKSPACE_PATHS.workspaceSuspended,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForNotSuspendedPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.permissions,
        }),
        lazy: () => import('#features/permissions/pages/FilespacePermissions/FilespacePermissions.tsx'),
        path: FILESPACE_PATHS.filespacePermissions,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        { OR: ['groupsRead', 'membersUpdate'] },
                    ],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.editPermissions,
        }),
        lazy: () => import('#features/permissions/pages/FsEntryPermissions/FsEntryPermissions.tsx'),
        path: FILESPACE_PATHS.filespacePermissionsFsEntry,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        { OR: ['membersUpdate', 'groupsRead'] },
                    ],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.configurePermissions,
        }),
        lazy: () => import('#features/permissions/pages/FilespacePermissionCreate/FilespacePermissionCreate.tsx'),
        path: FILESPACE_PATHS.filespacePermissionsFsEntryCreate,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        { OR: ['groupsRead', 'membersUpdate'] },
                    ],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.editPermissions,
        }),
        lazy: () => import('#features/permissions/pages/PrincipalPermissions/PrincipalPermissions.tsx'),
        path: FILESPACE_PATHS.filespacePermissionsPrincipal,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        { OR: ['membersUpdate', 'groupsRead'] },
                    ],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.configurePermissions,
        }),
        lazy: () => import('#features/permissions/pages/PrincipalPermissionCreate/PrincipalPermissionCreate.tsx'),
        path: FILESPACE_PATHS.filespacePermissionsPrincipalCreate,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(PrivilegeRouteGuard, {
                requiredPrivileges: {
                    AND: [
                        'canAdminFilespaces',
                        { OR: ['groupsRead', 'membersUpdate'] },
                    ],
                },
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceDashboard,
        }),
        lazy: Promise.resolve({
            default: () => _jsx(Navigate, { to: "dashboard" }),
        }),
        path: FILESPACE_PATHS.filespace,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceFooterLayout),
            wrapper(FilespacePermissionRouteGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceDashboard,
        }),
        lazy: () => import('#features/filespaces/pages/FilespaceDashboard.tsx'),
        path: FILESPACE_PATHS.filespaceDashboard,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.snapshots,
        }),
        lazy: () => import('#features/filespaces/pages/FilespaceSnapshots/FilespaceSnapshots.tsx'),
        path: FILESPACE_PATHS.filespaceSnapshots,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
            wrapper(RestrictedFilespaceRouteGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceSettings,
        }),
        lazy: () => import('#features/filespaces/pages/FilespaceSettingsBase.tsx'),
        path: FILESPACE_PATHS.filespaceSettings,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
            wrapper(RestrictedFilespaceRouteGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.guestLinks,
        }),
        lazy: () => import('#features/guest-links/pages/GuestLinks/GuestLinks.tsx'),
        path: FILESPACE_PATHS.filespaceGuestLinks,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(WorkspaceStatusRouteGuard, {
                predicateFn: disableForSuspendedPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(RestrictedFilespaceRouteGuard),
            wrapper(FilespaceAdminGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceLucidLinkConnect,
        }),
        lazy: () => import('#features/lucidlink-connect/pages/FilespaceLucidLinkConnect/FilespaceLucidLinkConnect.tsx'),
        path: FILESPACE_PATHS.filespaceLucidLinkConnect,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(FeatureFlagGuard, LucidLinkConnectFeatureFlagProperties),
            wrapper(WorkspaceNativeLayoutRouteGuard),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(FilespaceAdminGuard),
            wrapper(FilespaceLucidLinkConnectRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
            wrapper(RestrictedFilespaceRouteGuard),
        ],
    },
    {
        handle: setHandle({
            heading: HEADING_KEYS.filespaceLucidLinkConnect,
        }),
        lazy: () => import('#features/lucidlink-connect/pages/FilespaceLucidLinkConnectDataStore/FilespaceLucidLinkConnectDataStore.tsx'),
        path: FILESPACE_PATHS.filespaceLucidLinkConnectDataStore,
        wrappers: [
            wrapper(ProtectedRoute, { predicateFn: privateRoutePredicate }),
            wrapper(FeatureFlagGuard, LucidLinkConnectFeatureFlagProperties),
            wrapper(WorkspaceNativeLayoutRouteGuard),
            wrapper(HeaderLayout),
            wrapper(MemberRouteGuard),
            wrapper(WorkspaceNavigationLayout),
            wrapper(NoCodeLoginRoute),
            wrapper(WorkspaceAuthenticationRouteGuard, {
                predicateFn: needsWorkspaceAuthenticationPredicate,
            }),
            wrapper(FilespacePermissionRouteGuard),
            wrapper(FilespaceAdminGuard),
            wrapper(FilespaceLucidLinkConnectRouteGuard),
            wrapper(IsMigratingRouteGuard),
            wrapper(FilespaceFooterLayout),
            wrapper(RestrictedFilespaceRouteGuard),
        ],
    },
];
export const childRouteObjects = childRoutesConfig
    .filter(isNotNil)
    .map(getRouteObject);
